"use client";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Roboto } from "next/font/google";
import { type ReactNode } from "react";
import { useOAuthPending } from "./useOAuthPending";
export { OAUTH_NAVIGATION_TIMEOUT_MS } from "./useOAuthPending";

const robotoFonts = Roboto({
  weight: ["400"],
  subsets: ["latin"],
  display: "swap",
  style: "normal",
});

const DEFAULT_ERROR_MESSAGE = "Couldn't connect. Please try again.";

export interface OAuthProviderOption {
  /** Stable key driving the per-button loading state. */
  id: string;
  label: string;
  /**
   * Provider logo node, sized ~20px (e.g. `<GoogleLogo className="h-5 w-5" />`).
   * Inlined (not an <img src>) so it renders synchronously with the button —
   * the spinner swaps in for it while pending.
   */
  icon: ReactNode;
  /**
   * Kicks off sign-in and returns the underlying `signIn` promise so we can
   * await it and recover from a failure / hang. On success the browser
   * navigates away and the host unmounts.
   */
  signIn: () => Promise<unknown>;
}

interface Props {
  providers: OAuthProviderOption[];
  /** Disable all provider buttons (e.g. while a sibling action is pending). */
  disabled?: boolean;
  /** Inline error copy shown when an attempt fails. */
  errorMessage?: string;
  /**
   * Notified whenever the in-flight state flips, so a host can keep sibling
   * actions mutually exclusive with an OAuth attempt (e.g. disable a separate
   * "Connect AwardWallet" button while a provider is pending). Pass a stable
   * callback (e.g. a useState setter).
   */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * Renders the white "Continue with <provider>" OAuth buttons shared by the
 * sign-in and connect-accounts modals, with the loading / disable / inline-error
 * / stuck-recovery behavior that keeps the buttons from looking dead (GON-5788).
 *
 * Returns the error + buttons as siblings (no layout wrapper) so the host can
 * place its own buttons (e.g. "Continue with Email", "Connect AwardWallet") in
 * the same flex column and keep a consistent gap.
 */
export function OAuthProviderButtons({
  providers,
  disabled = false,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  onPendingChange,
}: Props) {
  const { pendingId, error, handleSignIn } = useOAuthPending({
    errorMessage,
    onPendingChange,
  });

  return (
    <>
      {error && (
        <p
          role="alert"
          className="w-[240px] text-center text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {providers.map((option) => {
        const isLoading = pendingId === option.id;
        return (
          <Button
            key={option.id}
            variant="outline"
            onClick={() => handleSignIn(option)}
            disabled={disabled || pendingId !== null}
            className="font-roboto flex w-[240px] justify-start gap-2.5 border border-input bg-pure-white px-3 py-2.5 font-medium tracking-[0.25px]"
          >
            {/* Swap the provider logo for the spinner in place — `sm` is a 20px
                spinner, the same slot as the logo, so the loading state doesn't
                shift the button layout. text-pure-black keeps it visible on the
                white button (the inherited text-primary is near-white in dark
                mode). */}
            {isLoading ? (
              <Spinner size="sm" className="text-pure-black" />
            ) : (
              option.icon
            )}
            <span className={cn("text-pure-black", robotoFonts.className)}>
              {option.label}
            </span>
          </Button>
        );
      })}
    </>
  );
}
